import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '../../../../environments/environment';
import {
  SlackOAuthInstallResponseDto,
  SlackStatusResponseDto,
  SlackChannelsResponseDto,
  SlackProjectChannelResponseDto,
  SlackProjectChannelRequestDto,
  SlackProjectStatusResponseDto,
  SlackAffectedProjectsResponseDto,
  SlackMessageResponseDto,
} from '../models/slack.dto';

@Injectable({
  providedIn: 'root'
})
export class SlackService {
  private readonly apiUrl = `${environment.apiHost}/api`;

  constructor(private readonly http: HttpClient) {}

  public getOAuthInstallUrl(organizationId: string, projectId?: string): Observable<SlackOAuthInstallResponseDto> {
    const params: Record<string, string> = { organization_id: organizationId };
    if (projectId) {
      params['project_id'] = projectId;
    }
    return this.http.get<SlackOAuthInstallResponseDto>(
      `${this.apiUrl}/slack/oauth/install/`,
      { params }
    );
  }

  public getSlackStatus(organizationId: string): Observable<SlackStatusResponseDto> {
    return this.http.get<SlackStatusResponseDto>(
      `${this.apiUrl}/slack/${organizationId}/status/`
    );
  }

  public listChannels(organizationId: string): Observable<SlackChannelsResponseDto> {
    return this.http.get<SlackChannelsResponseDto>(
      `${this.apiUrl}/slack/${organizationId}/channels/`
    );
  }

  public getProjectChannel(projectId: string): Observable<SlackProjectChannelResponseDto> {
    return this.http.get<SlackProjectChannelResponseDto>(
      `${this.apiUrl}/slack/projects/${projectId}/channel/`
    );
  }

  public setProjectChannel(
    projectId: string,
    data: SlackProjectChannelRequestDto
  ): Observable<SlackProjectChannelResponseDto> {
    return this.http.post<SlackProjectChannelResponseDto>(
      `${this.apiUrl}/slack/projects/${projectId}/channel/`,
      data
    );
  }

  public removeProjectChannel(projectId: string): Observable<SlackMessageResponseDto> {
    return this.http.delete<SlackMessageResponseDto>(
      `${this.apiUrl}/slack/projects/${projectId}/channel/`
    );
  }

  public testNotification(projectId: string): Observable<SlackMessageResponseDto> {
    return this.http.post<SlackMessageResponseDto>(
      `${this.apiUrl}/slack/projects/${projectId}/test/`,
      {}
    );
  }

  public disconnectSlack(organizationId: string): Observable<SlackMessageResponseDto> {
    return this.http.delete<SlackMessageResponseDto>(
      `${this.apiUrl}/slack/${organizationId}/disconnect/`
    );
  }

  public getAffectedProjects(organizationId: string): Observable<SlackAffectedProjectsResponseDto> {
    return this.http.get<SlackAffectedProjectsResponseDto>(
      `${this.apiUrl}/slack/${organizationId}/affected-projects/`
    );
  }

  // --- Project-level endpoints ---

  public getProjectStatus(projectId: string): Observable<SlackProjectStatusResponseDto> {
    return this.http.get<SlackProjectStatusResponseDto>(
      `${this.apiUrl}/slack/projects/${projectId}/status/`
    );
  }

  /**
   * The channels the project can pick from. Naming a workspace link lists the channels of a
   * workspace the project is about to move to; without one the backend uses the project's
   * connection, or its organization's only link.
   */
  public getProjectChannels(projectId: string, workspaceLinkId?: string): Observable<SlackChannelsResponseDto> {
    const params: Record<string, string> = workspaceLinkId ? { workspace_link_id: workspaceLinkId } : {};
    return this.http.get<SlackChannelsResponseDto>(
      `${this.apiUrl}/slack/projects/${projectId}/channels/`,
      { params }
    );
  }

  public disconnectProjectSlack(projectId: string): Observable<SlackMessageResponseDto> {
    return this.http.delete<SlackMessageResponseDto>(
      `${this.apiUrl}/slack/projects/${projectId}/disconnect/`
    );
  }
}
